import { IF, NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHProcurementOffer, RHProcurementPartOffer, RHProcurementPublic } from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHConflictOfInterestBannerView } from "../../Custom components/RH/RHConflictOfInterestBannerView"
import { RHPanelView } from "../../Custom components/RH/RHPanelView"
import { RHVerticalStackView } from "../../Custom components/RH/RHVerticalStackView"
import { RHButtons, RHColors, RHFont, RHRadius, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHMakeOfferViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import type { RHOfferCapabilities } from "../../SocketClientFunctions"
import { OfferPartCardView } from "./OfferPartCardView"


export class ConfirmProcurementOfferView extends RHPanelView {
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header3)
        .configuredWithObject({
            text: "Pakkumuse eelvaade",
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColor.dark.stringValue,
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXXLarge,
                fontWeight: "500",
                lineHeight: RHFont.lineHeightHeading
            }
        })
    
    readonly subtitleLabel = new UITextView()
        .configuredWithObject({
            text: "Palun kontrolli oma pakkumused üle enne kinnitamist.",
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColor.textMuted.stringValue,
                fontSize: RHFont.sizeMedium,
                fontWeight: "400",
                lineHeight: RHFont.lineHeightBase
            }
        })
    
    readonly infoBanner = new RHAlertBannerView()
        .configureBannerWithObject({
            backgroundColor: RHColors.warningAmberSurfaceDraft,
            borderColor: RHColors.warningAmberBorderDraft,
            textColor: RHColors.warningAmberTextDeep,
            fontSize: RHFont.sizeSmall,
            fontWeight: "500",
            padding: { top: 10, right: 12, bottom: 10, left: 14 }
        })
        .configuredWithObject({
            style: {
                borderRadius: `${RHRadius.large}px`
            }
        })
    
    readonly errorBanner = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Error
        })

    readonly conflictOfInterestBannerView = new RHConflictOfInterestBannerView()

    readonly previewStackView = new RHVerticalStackView()

    readonly totalPriceLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColor.dark.stringValue,
                fontSize: RHFont.sizeLarge,
                fontWeight: "600",
                lineHeight: RHFont.lineHeightBase
            }
        })
    
    readonly backButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "← Tagasi muutmisele" },
            variant: RHButtons.exitUtilityVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            }
        })
    
    readonly confirmButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "✓ Esita pakkumused" },
            variant: RHMakeOfferViewTokens.confirmButtonVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            }
        })

    readonly deleteDraftOfferButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Kustuta mustand" },
            variant: "softDanger",
            hidden: YES,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            }
        })
    
    didSubmitProcurementOffer?: (submittedOffer: RHProcurementOffer) => void
    
    static _submitButtonTitle(offerCount: number): string {
        if (offerCount === 1) {
            return "✓ Esita pakkumus"
        }
        return "✓ Esita pakkumused"
    }
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (this.backButton.superview === this) { items.push(this.backButton) }
        if (this.confirmButton.superview === this) { items.push(this.confirmButton) }
        if (this.deleteDraftOfferButton.superview === this && !this.deleteDraftOfferButton.hidden) {
            items.push(this.deleteDraftOfferButton)
        }
        return items
    })
    
    _previewRows: OfferPartCardView[] = []
    _draftOffer?: RHProcurementOffer
    _isSubmitting = NO
    _offerCapabilities?: RHOfferCapabilities
    _procurement?: RHProcurementPublic
    _areOfferActionsEnabled = YES
    _hasOffersForFinalizedParts = NO
    
    constructor(elementID?: string) {
        super(elementID)
        // No subviews added here — configure() builds the hierarchy fresh each time
        this.previewStackView.rowGap = RHSpacing.medium
        
        this.confirmButton.addTargetForControlEvent(
            RHButton.controlEvent.PointerUpInside,
            () => { this._submitOffer() }
        )
    }

    set areOfferActionsEnabled(areEnabled: boolean) {
        this._areOfferActionsEnabled = areEnabled
        this._applyEditCapability()
        this._applySubmitCapability()
    }

    set offerCapabilities(capabilities: RHOfferCapabilities | undefined) {
        this._offerCapabilities = capabilities
        this._applyEditCapability()
        this._applySubmitCapability()
    }
    
    configure(
        procurement: RHProcurementPublic,
        draftOffer: RHProcurementOffer,
        partOffersByPartId: Map<string, RHProcurementPartOffer[]>
    ) {
        this._draftOffer = draftOffer
        this._procurement = procurement
        this._hasOffersForFinalizedParts = (draftOffer.partOffers as RHProcurementPartOffer[]).some(partOffer =>
            (procurement.finalizedPartIds ?? []).includes(partOffer.partId.toString())
        )
        const procurementId = procurement._id?.toString()
        const offerId = draftOffer._id?.toString()
        if (procurementId && offerId) {
            void this.conflictOfInterestBannerView.load({
                procurementId,
                fromSide: "offer",
                offerId
            }).then(() => this._applySubmitCapability())
        }
        else {
            this.conflictOfInterestBannerView.clear()
        }
        
        // Wipe and rebuild so no preview rows from an earlier draft remain.
        this._previewRows.forEach(row => {
            row.removeFromSuperview()
        })
        this.subviews.everyElement.removeFromSuperview()
        this._previewRows = []

        // Build the complete hierarchy before configuring the cards. This lets
        // card-level layout invalidation reach the panel whenever it is already
        // visible, while wasAddedToViewTree() covers delayed attachment.
        this.titleLabel.addedAsSubviewToView(this)
        this.subtitleLabel.addedAsSubviewToView(this)
        this.conflictOfInterestBannerView.addedAsSubviewToView(this)
        this.previewStackView.addedAsSubviewToView(this)
        this.deleteDraftOfferButton.removeFromSuperview()
        this.deleteDraftOfferButton.addedAsSubviewToView(this)
        
        const totalPricesByCurrency = new Map<string, number>()
        // price.amount is only ever the 0 sentinel while a part's real price
        // lives solely in its encrypted envelope (see _decryptionFailed in
        // OffersController.ts) — summing it in that state would silently
        // produce a wrong-looking real total (e.g. "0,00 €") instead of
        // admitting the total itself couldn't be computed.
        let anyPartDecryptionFailed = false
        const partCount = procurement.parts.length
        for (const part of procurement.parts) {
            if ((procurement.finalizedPartIds ?? []).includes(part._id.toString())) {
                continue
            }
            const partOffers = partOffersByPartId.get(part._id.toString()) ?? []
            for (const partOffer of partOffers) {
                if (partOffer.isCancelled) {
                    continue
                }
                if (partOffer._decryptionFailed) {
                    anyPartDecryptionFailed = true
                }
                else {
                    const currency = partOffer.price.currency || "EUR"
                    totalPricesByCurrency.set(
                        currency,
                        (totalPricesByCurrency.get(currency) ?? 0) + partOffer.price.amount
                    )
                }
                // Same per-part card as the submitted/result views, but with no
                // footer — the confirm preview shows no "esitatud" banner and no
                // edit/cancel actions (the offer is not submitted yet).
                const card = new OfferPartCardView()
                card.addedAsSubviewToView(this.previewStackView)
                card.configureWithPartOffer(part, partOffer, partCount)
                this._previewRows.push(card)
            }
        }

        if (this._previewRows.length === 0) {
            this.previewStackView.removeFromSuperview()
        }
        else {
            this.totalPriceLabel.text = anyPartDecryptionFailed
                ? "Pakkumuse kogusumma (KM-ta): ???"
                : "Pakkumuse kogusumma (KM-ta): " + Array.from(
                    totalPricesByCurrency,
                    ([currency, amount]) => RHEuroFormatter.formattedCurrency(amount, { currency })
                ).join(" + ")
            this.totalPriceLabel.addedAsSubviewToView(this)
        }
        this.confirmButton.titleLabel.text = ConfirmProcurementOfferView._submitButtonTitle(this._previewRows.length)
        this._applyEditCapability()
        this._applySubmitCapability()
        
        this.setNeedsLayoutUpToRootView()
    }


    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        this._previewRows.forEach(row => row.setNeedsLayout())
        this.previewStackView.setNeedsLayout()
        this.setNeedsLayout()
    }
    
    _showErrorBanner(message: string) {
        this.errorBanner.text = "⚠️ " + message
        this.errorBanner.addedAsSubviewToView(this)
        this._applyEditCapability()
        this._applySubmitCapability()
        this.setNeedsLayout()
    }
    
    _hideErrorBanner() {
        this.errorBanner.removeFromSuperview()
    }
    
    async _submitOffer() {
        if (!this._draftOffer?._id || this._isSubmitting) { return }
        if (this._hasOffersForFinalizedParts || this._previewRows.length === 0) { return }
        if (this.conflictOfInterestBannerView.status === "blocked") { return }
        const unavailableMessage = this._submissionUnavailabilityMessage()
        if (unavailableMessage) {
            this._showErrorBanner(unavailableMessage)
            return
        }
        if (!this._canSubmitOffer()) {
            this._showErrorBanner("Sul puudub pakkumuse esitamise õigus.")
            return
        }
        
        this._hideErrorBanner()
        this._isSubmitting = YES
        this.confirmButton.enabled = NO
        this.confirmButton.titleLabel.text = "Esitamine..."
        this.backButton.enabled = NO
        
        try {
            const submittedOffer = await RHCore.sharedInstance.offerStore.submitOffer(
                this._draftOffer._id + ""
            )
            
            this.didSubmitProcurementOffer?.(submittedOffer)
        }
        catch (error: unknown) {
            console.error("Error submitting offer:", error)
            let errorMessage = "Viga pakkumuse esitamisel"
            if (error instanceof Error) {
                errorMessage = error.message
            }
            this._showErrorBanner(errorMessage)
        }
        finally {
            this._isSubmitting = NO
            this._applyEditCapability()
            this._applySubmitCapability()
            this.confirmButton.titleLabel.text = ConfirmProcurementOfferView._submitButtonTitle(this._previewRows.length)
        }
    }


    // Older rolling-deployment responses omitted capabilities and relied on the server guard,
    // so absent capabilities must default to allowed rather than denied.
    _canSubmitOffer(): boolean {
        return !this._hasOffersForFinalizedParts && this._previewRows.length > 0 &&
            this.conflictOfInterestBannerView.status !== "blocked" &&
            !this._submissionUnavailabilityMessage() && (!this._offerCapabilities || this._offerCapabilities.canSubmitOffer === YES)
    }


    _canEditOffer(): boolean {
        return !this._submissionUnavailabilityMessage() && (!this._offerCapabilities || this._offerCapabilities.canEdit === YES)
    }


    _submissionUnavailabilityMessage(): string {
        if (this._procurement?.deadlineDate && new Date(this._procurement.deadlineDate).getTime() <= Date.now()) {
            return "Pakkumuste esitamise tähtaeg on möödas."
        }
        if (!this._areOfferActionsEnabled || (this._procurement && this._procurement.status !== "published")) {
            return "Hange on lõppenud. Pakkumuste esitamine ei ole enam võimalik."
        }
        return ""
    }


    _applyEditCapability() {
        if (this._canEditOffer()) {
            if (this.backButton.superview !== this) {
                this.backButton.addedAsSubviewToView(this)
            }
            this.backButton.enabled = !this._isSubmitting
        }
        else {
            this.backButton.removeFromSuperview()
        }
    }


    _applySubmitCapability() {
        if (!this.confirmButton) {
            return
        }
        if (!this._submissionUnavailabilityMessage() && (!this._offerCapabilities || this._offerCapabilities.canSubmitOffer === YES)) {
            if (this.confirmButton.superview !== this) {
                this.confirmButton.addedAsSubviewToView(this)
            }
            this.confirmButton.enabled = !this._isSubmitting && this._canSubmitOffer()
        }
        else {
            this.confirmButton.removeFromSuperview()
        }
        const unavailableMessage = this._submissionUnavailabilityMessage()
        this.infoBanner.removeFromSuperview()
        if (unavailableMessage) {
            this.infoBanner.text = unavailableMessage
        }
        else if (this._offerCapabilities && this._offerCapabilities.canEdit !== YES) {
            this.infoBanner.text =
                "Pakkumus on ainult vaatamiseks. Muutmine ja esitamine on lubatud ainult selleks määratud kasutajatele."
        }
        else if (this._offerCapabilities && this._offerCapabilities.canSubmitOffer !== YES) {
            this.infoBanner.text =
                "Pakkumust saavad koostada ja esitada ainult vastutav isik ning volitatud isikud."
        }
        else if (this._hasOffersForFinalizedParts && this._previewRows.length === 0) {
            this.infoBanner.text =
                "Kõik mustandis pakutud osad on lõpetatud. Neile ei saa enam pakkumust esitada. Avatud osale pakkumiseks vali „Tagasi muutmisele“."
        }
        else if (this._hasOffersForFinalizedParts) {
            this.infoBanner.text =
                "Mustand sisaldab lõpetatud osade pakkumusi. Eelvaates on ainult avatud osad. Vali „Tagasi muutmisele“ ja salvesta mustand uuesti; lõpetatud osade pakkumused jäetakse välja."
        }
        else {
            return
        }
        this.infoBanner.addedAsSubviewToView(this)
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)
        
        const contentFrame = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding * 0.5, this.subtitleLabel)
            .assignedAsFrameOfView(this.subtitleLabel)
            .IF(this.infoBanner.superview === this)
            .rectangleForNextRow(padding, this.infoBanner)
            .assignedAsFrameOfView(this.infoBanner)
            .ENDIF()
            .rectangleForNextRow(
                IF(this.conflictOfInterestBannerView.isVisible)(() => padding).ELSE(() => 0),
                this.conflictOfInterestBannerView
            )
            .assignedAsFrameOfView(this.conflictOfInterestBannerView)
            .IF(this._previewRows.length > 0)
            .rectangleForNextRow(padding, this.previewStackView)
            .assignedAsFrameOfView(this.previewStackView)
            .rectangleForNextRow(padding, this.totalPriceLabel)
            .assignedAsFrameOfView(this.totalPriceLabel)
            .ENDIF()
            .IF(this.errorBanner.superview === this)
            .rectangleForNextRow(padding, this.errorBanner)
            .assignedAsFrameOfView(this.errorBanner)
            .ENDIF()
        
        const actionButtons = [this.backButton, this.confirmButton, this.deleteDraftOfferButton]
            .filter(button => button.superview === this && !button.hidden)
        if (actionButtons.length > 0) {
            const buttonRowFrame = contentFrame.rectangleForNextRow(
                padding * 1.5,
                RHMakeOfferViewTokens.actionButtonHeight
            )
            const buttonSlotWidth = (
                buttonRowFrame.width - RHSpacing.medium * (actionButtons.length - 1)
            ) / actionButtons.length
            const buttonWidths = actionButtons.map(button => Math.min(
                Math.max(
                    button.titleLabel.intrinsicContentWidth(RHMakeOfferViewTokens.actionButtonHeight) +
                    RHButtons.compactPrimaryContentPadding * 2 +
                    RHSpacing.large,
                    RHMakeOfferViewTokens.actionButtonMinWidth
                ),
                RHMakeOfferViewTokens.actionButtonMaxWidth,
                buttonSlotWidth
            ))
            const centeredButtonRowWidth = buttonWidths.reduce(
                (totalWidth, buttonWidth) => totalWidth + buttonWidth,
                RHSpacing.medium * (actionButtons.length - 1)
            )

            buttonRowFrame
                .rectangleWithWidth(centeredButtonRowWidth, 0.5)
                .framesByDistributingViewsAsRow(
                    actionButtons,
                    RHSpacing.medium,
                    buttonWidths
                )
        }
    }
    
    override intrinsicContentHeight(constrainingWidth = 0): number {
        const padding = this.core.paddingLength
        const contentWidth = Math.max(0, (constrainingWidth || this.bounds.width) - padding * 2)
        
        const height =
            padding +
            this.titleLabel.intrinsicContentHeight(contentWidth) +
            padding * 0.5 + this.subtitleLabel.intrinsicContentHeight(contentWidth) +
            IF(this.infoBanner.superview === this)(() =>
                padding + this.infoBanner.intrinsicContentHeight(contentWidth)
            ).ELSE(() => 0) +
            IF(this.conflictOfInterestBannerView.isVisible)(() =>
            padding + this.conflictOfInterestBannerView.intrinsicContentHeight(contentWidth)
            ).ELSE(() => 0) +
            IF(this._previewRows.length > 0)(() =>
                padding + this.previewStackView.intrinsicContentHeight(contentWidth) +
                padding + this.totalPriceLabel.intrinsicContentHeight(contentWidth)
            ).ELSE(() => 0) +
            IF(this.errorBanner.superview === this)(() =>
                padding + this.errorBanner.intrinsicContentHeight(contentWidth)
            ).ELSE(() => 0) +
            IF([this.backButton, this.confirmButton, this.deleteDraftOfferButton].some(button =>
                button.superview === this && !button.hidden
            ))(() =>
                padding * 1.5 + RHMakeOfferViewTokens.actionButtonHeight
            ).ELSE(() => 0)
        return height + padding
    }
    
}
